import { CropRecord, ParserResult } from "pages/admin/experiments/cropExtraction/types";

declare global {
  interface Window {
    shp?: (input: ArrayBuffer | string) => Promise<unknown>;
  }
}

export type DbfRow = Record<string, unknown>;

const lower = (s: string) => s.toLowerCase();

// Case-insensitive column lookup. Most DBF schemas mix UPPER and lower case
// names depending on the export; we tolerate both by lowering the candidates.
export const findField = (rows: DbfRow[], candidates: string[]): string | undefined => {
  if (rows.length === 0) return undefined;
  const lowered = new Map(Object.keys(rows[0]).map((k) => [lower(k), k]));
  for (const c of candidates) {
    const hit = lowered.get(lower(c));
    if (hit) return hit;
  }
  return undefined;
};

export const toString = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value.trim();
  return String(value).trim();
};

export const toNumber = (value: unknown): number => {
  if (value === null || value === undefined) return 0;
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  const s = String(value).trim().replace(/,/g, ".");
  if (!s) return 0;
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
};

export interface NamedDbfTable {
  basename: string; // e.g. "Nutzung"
  rows: DbfRow[];
}

const stripExt = (name: string) => name.replace(/\.[^.]+$/, "");

const baseName = (path: string) => path.split("/").pop() || path;

interface ShpFeature {
  type?: string;
  properties?: DbfRow;
  geometry?: unknown;
}

interface ShpFeatureCollection {
  type: string;
  features?: ShpFeature[];
  fileName?: string;
}

const isFeatureCollection = (value: unknown): value is ShpFeatureCollection =>
  typeof value === "object" && value !== null && (value as { type?: unknown }).type === "FeatureCollection";

// Reads every .dbf inside the ZIP via the global window.shp loader. shpjs
// returns either a single FeatureCollection or an array of them when multiple
// shapefile stems exist. The DBF row data is exposed on `feature.properties`.
export const readAllDbfs = async (zipBuffer: ArrayBuffer): Promise<NamedDbfTable[]> => {
  if (typeof window === "undefined" || typeof window.shp !== "function") {
    throw new Error("shp.js not loaded — addShapeProcessor must be called first");
  }
  const result = (await window.shp(zipBuffer)) as ShpFeatureCollection | ShpFeatureCollection[];
  const collections: ShpFeatureCollection[] = Array.isArray(result) ? result : [result];

  const tables: NamedDbfTable[] = [];
  for (const fc of collections) {
    if (isFeatureCollection(fc)) {
      const fileName = fc.fileName ? stripExt(baseName(fc.fileName)) : "shape";
      const rows = (fc.features ?? []).map((f) => f.properties).filter((p): p is DbfRow => Boolean(p));
      tables.push({ basename: fileName, rows });
    }
  }
  return tables;
};

export const findTable = (tables: NamedDbfTable[], predicate: (basename: string) => boolean): NamedDbfTable | undefined =>
  tables.find((t) => predicate(lower(t.basename)));

// Looks for the application year inside the rows of a DBF table. Tries the
// canonical year columns first, then falls back to scanning date columns
// (ELAN-NRW often only has `dat_bearb`, FIONA only has `bearbeitungsdatum`).
// Returns the most-frequent year so a single stale row can't outvote the
// real Antragsjahr.
const YEAR_MIN = 2015;
const YEAR_MAX = new Date().getFullYear() + 1;
export const YEAR_DBF_COLUMNS = ["ANTRAGSJAHR", "WIRTSCHAFTSJAHR", "JAHR", "SAISON", "YEAR", "AJ"];
export const DATE_DBF_COLUMNS = ["DAT_BEARB", "BEARBEITUNGSDATUM", "ERSTELLDATUM", "DATUM", "CREATED_DATE", "MODIFIED_DATE"];

const inRange = (n: number) => Number.isFinite(n) && n >= YEAR_MIN && n <= YEAR_MAX;

const yearFromValue = (value: unknown): number | undefined => {
  if (value === null || value === undefined) return undefined;
  if (typeof value === "number") return inRange(value) ? Math.trunc(value) : undefined;
  if (value instanceof Date) {
    const y = value.getFullYear();
    return inRange(y) ? y : undefined;
  }
  const str = String(value).trim();
  if (!str) return undefined;
  // Direct 4-digit year cell.
  if (/^\d{4}$/.test(str)) {
    const n = Number(str);
    return inRange(n) ? n : undefined;
  }
  // Any embedded 4-digit year token bounded by non-digits.
  const m = str.match(/(?<![0-9])(20[1-3]\d)(?![0-9])/);
  if (m) {
    const n = Number(m[1]);
    if (inRange(n)) return n;
  }
  return undefined;
};

export const findYearInDbfRows = (table: NamedDbfTable | undefined): number | undefined => {
  if (!table || table.rows.length === 0) return undefined;
  const yearCol = findField(table.rows, YEAR_DBF_COLUMNS);
  const dateCol = findField(table.rows, DATE_DBF_COLUMNS);
  if (!yearCol && !dateCol) return undefined;

  const counts = new Map<number, number>();
  for (const row of table.rows) {
    const candidate = yearCol ? yearFromValue(row[yearCol]) : undefined;
    const fromDate = !candidate && dateCol ? yearFromValue(row[dateCol]) : undefined;
    const year = candidate ?? fromDate;
    if (year) counts.set(year, (counts.get(year) ?? 0) + 1);
  }
  if (counts.size === 0) return undefined;

  let best: number | undefined;
  let bestCount = -1;
  for (const [year, count] of counts) {
    if (count > bestCount || (count === bestCount && (best === undefined || year > best))) {
      best = year;
      bestCount = count;
    }
  }
  return best;
};

// Absorbs the boilerplate every DBF-based parser was repeating: load all DBFs
// from the ZIP, pick the right one, bail with a friendly warning if it's
// missing/empty, run a per-row extractor, and return a populated ParserResult.
//
// `buildExtractor(table)` resolves columns once (using `findField`) and returns
// a function that maps a row → CropRecord (or null to skip the row). Callers
// don't need to call describeCrop; the orchestrator's enrichCrops handles that.
export interface DbfParserOptions {
  pickTable: (tables: NamedDbfTable[]) => NamedDbfTable | undefined;
  buildExtractor: (table: NamedDbfTable) => (row: DbfRow) => CropRecord | null;
  notFoundMessage: string;
  emptyMessage?: string;
  method?: string;
}

export const runDbfParser = async (zipBuffer: ArrayBuffer, opts: DbfParserOptions): Promise<ParserResult> => {
  const method = opts.method ?? "dbf_column";
  const tables = await readAllDbfs(zipBuffer);
  const table = opts.pickTable(tables);
  if (!table || table.rows.length === 0) {
    return { method, crops: [], warnings: [opts.notFoundMessage] };
  }

  const extractor = opts.buildExtractor(table);
  const crops: CropRecord[] = [];
  for (const row of table.rows) {
    const r = extractor(row);
    if (r) crops.push(r);
  }

  const warnings: string[] = [];
  if (crops.length === 0) warnings.push(opts.emptyMessage ?? `${opts.notFoundMessage} (no readable rows)`);

  // Look across all tables — some exports keep the year column on a sibling
  // header table rather than on the schlag table we just parsed. Picking the
  // first hit preserves "schlag table wins" precedence because we visit it
  // before the others below.
  const yearTables = [table, ...tables.filter((t) => t !== table)];
  let year: number | undefined;
  for (const t of yearTables) {
    const candidate = findYearInDbfRows(t);
    if (candidate) {
      year = candidate;
      break;
    }
  }

  return { method, crops, warnings, year };
};

// Polls every 50ms for the global shp.js loader. Implemented with chained
// setTimeout calls (instead of an awaited Promise inside a loop) to satisfy
// `no-await-in-loop` while still bailing out after `timeoutMs`.
export const ensureShpLoaded = (timeoutMs = 5000): Promise<void> =>
  new Promise<void>((resolve, reject) => {
    const start = Date.now();
    const check = () => {
      if (typeof window !== "undefined" && typeof window.shp === "function") {
        resolve();
        return;
      }
      if (Date.now() - start > timeoutMs) {
        reject(new Error("shp.js failed to load within 5s"));
        return;
      }
      setTimeout(check, 50);
    };
    check();
  });
